import { useEffect, useState } from "react";
import { advisoryApi, type Json } from "../advisoryApi";
import { dateTime, isRecord, stepLabel } from "../format";
import { Modal, SaveBar } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

// Renderers for the component types this tenant's Investment Advice flow does
// not use today (classification, preferences, AML) plus the read-only
// sustainability match and the generic fallback — so a flow change never
// dead-ends the workbench (§6).

/** `sustainability_status` — read-only match of the portfolio against the preferences. */
export function SustainabilityStatusStep({ sessionId, addr }: StepProps) {
  const [data, setData] = useState<Json | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!addr.goalId || !addr.portfolioId) return;
    advisoryApi
      .portfolioSustainability(sessionId, addr.goalId, addr.portfolioId)
      .then((d) => setData(d.data))
      .catch((e) => setErr((e as Error).message));
  }, [sessionId, addr.goalId, addr.portfolioId]);

  if (err) return <div className="banner-error">{err}</div>;
  if (!data) return <p className="muted">Checking the sustainability match…</p>;
  const ok = data.status === "ok";
  const snap = isRecord(data.snapshot) ? data.snapshot : {};
  const products = Array.isArray(snap.products) ? snap.products.filter(isRecord) : [];
  return (
    <section className="adv-step-card">
      <ul className="check-list">
        <li className={ok ? "check-row check-pass" : "check-row check-warn"}>
          <span className="check-icon">{ok ? "✓" : "!"}</span>
          <span>
            {ok ? (
              <strong>The portfolio matches the client's sustainability preferences.</strong>
            ) : (
              <>
                <strong>The portfolio does not fully match the preferences.</strong> Accepting the difference is a decision made in
                Quantfolio. Alternatively, record sustainability as not important if that reflects the client.
              </>
            )}
          </span>
        </li>
      </ul>
      {products.length > 0 && (
        <table className="table adv-table-compact">
          <thead>
            <tr>
              <th>Fund</th>
              <th className="num">Weight</th>
              <th>Meets preferences</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => (
              <tr key={i}>
                <td className="strong">{String(p.name ?? "—")}</td>
                <td className="num">{p.weight != null ? `${Number(p.weight).toFixed(0)} %` : "—"}</td>
                <td>{p.meets ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const PERSON_QUESTIONS: { key: string; label: string }[] = [
  { key: "wantsOptUp", label: "The client asks to be treated as a professional client" },
  { key: "hasSignificantTransactions", label: "Significant transactions: on average 10 per quarter over the last four quarters" },
  { key: "hasLargePortfolio", label: "Financial instrument portfolio above EUR 500 000" },
  { key: "hasFinanceExperience", label: "Works or has worked in the financial sector in a relevant role" },
];

/** `client_classification` — MiFID classification (person answers), with "use previous". */
export function ClassificationStep({ sessionId, steps, readOnly, onSaved }: StepProps) {
  const load = useStepLoad<{ clientType: string; current: Json | null; previous: Json | null }>(sessionId, "client-classification", {});
  const saver = useSaver(onSaved);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [base, setBase] = useState("");

  useEffect(() => {
    const input = load.data?.current && isRecord(load.data.current.inputData) ? load.data.current.inputData : null;
    const a = input && isRecord(input.personAnswers) ? (input.personAnswers as Record<string, boolean>) : {};
    setAnswers(a);
    setBase(JSON.stringify(a));
  }, [load.data]);

  if (load.loading && !load.data) return <p className="muted">Loading classification…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;
  if (load.data && load.data.clientType !== "Person") return <GenericStep steps={steps} />;

  const dirty = JSON.stringify(answers) !== base;
  const save = async () => {
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "client-classification", {}, { personAnswers: answers }));
    if (r) setBase(JSON.stringify(answers));
  };
  const reuse = async () => {
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "client-classification", {}, { action: "reuse_previous" }));
    if (r) load.reload();
  };
  return (
    <section className="adv-step-card">
      {load.data?.previous && !readOnly && (
        <div className="adv-prefill-note">
          <span>The client has been classified before.</span>
          <button type="button" className="btn btn-ghost adv-btn" onClick={reuse} disabled={saver.saving}>
            Use previous answer
          </button>
        </div>
      )}
      <div className="adv-checks">
        {PERSON_QUESTIONS.map((q) => (
          <label key={q.key} className="adv-check-inline">
            <input
              type="checkbox"
              checked={!!answers[q.key]}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.checked }))}
              disabled={readOnly}
            />
            {q.label}
          </label>
        ))}
      </div>
      <SaveBar onSave={save} saving={saver.saving} dirty={dirty} error={saver.error} readOnly={readOnly} />
    </section>
  );
}

/** `trapets_lookup` / `enin_lookup` — stored screening plus an explicit (metered) run. */
export function AmlStep({ sessionId, steps, readOnly, onSaved }: StepProps) {
  const isEnin = steps.some((s) => s.componentType === "enin_lookup");
  const load = useStepLoad<{ current: Json | null }>(sessionId, isEnin ? "enin-lookup" : "trapets-lookup", {});
  const [confirm, setConfirm] = useState(false);
  const [ssn, setSsn] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      await advisoryApi.runAml(sessionId, isEnin ? "enin" : "trapets", ssn ? { ssn } : {});
      const s = await advisoryApi.session(sessionId);
      onSaved(s);
      load.reload();
      setConfirm(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const current = load.data?.current;
  const calc = current && isRecord(current.calculatedData) ? current.calculatedData : null;
  return (
    <section className="adv-step-card">
      {load.error && <div className="banner-error">{load.error}</div>}
      {calc ? (
        <dl className="info-grid">
          {Object.entries(calc)
            .filter(([, v]) => typeof v !== "object")
            .slice(0, 8)
            .map(([k, v]) => (
              <div className="info-row" key={k}>
                <dt>{k}</dt>
                <dd>{String(v)}</dd>
              </div>
            ))}
        </dl>
      ) : (
        <p className="muted">No screening has been run for this advice yet.</p>
      )}
      {!readOnly && (
        <div className="adv-savebar">
          {!isEnin && (
            <label className="adv-inline-field">
              National id
              <input value={ssn} onChange={(e) => setSsn(e.target.value)} placeholder="11 digits" />
            </label>
          )}
          <button type="button" className="btn btn-primary adv-btn" onClick={() => setConfirm(true)}>
            Run screening
          </button>
        </div>
      )}
      {confirm && (
        <Modal title="Run the screening?" onClose={() => setConfirm(false)}>
          <p>This runs a paid screening against the sanctions and PEP lists for this client.</p>
          {err && <div className="banner-error">{err}</div>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost adv-btn" onClick={() => setConfirm(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary adv-btn" onClick={run} disabled={busy}>
              {busy ? "Screening…" : "Run screening"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

/**
 * Generic fallback: any component type the workbench has no renderer for.
 * Shows what QP stores and says where to finish it — never a dead end.
 */
export function GenericStep({ steps }: Pick<StepProps, "steps"> & Partial<StepProps>) {
  return (
    <section className="adv-step-card">
      <ul className="check-list">
        {steps.map((s, i) => (
          <li key={s.componentId ?? i} className={s.status === "complete" ? "check-row check-pass" : "check-row adv-check-open"}>
            <span className="check-icon">{s.status === "complete" ? "✓" : ""}</span>
            <span>
              <strong>{stepLabel(s)}</strong>
              <span className="muted">
                {" "}
                · {s.status === "complete" ? "complete" : s.status === "not_applicable" ? "not applicable" : "open"}
              </span>
              {s.status === "open" && (
                <div className="muted">This step isn't available in the workbench yet — complete it in Quantfolio.</div>
              )}
            </span>
          </li>
        ))}
        {steps.length === 0 && (
          <li className="check-row adv-check-open">
            <span className="check-icon" />
            <span>This step isn't available in the workbench yet.</span>
          </li>
        )}
      </ul>
    </section>
  );
}

/** `client_preferences` — QP returns an empty option list for this flow; show the options when there are any. */
export function PreferencesStep(props: StepProps) {
  const load = useStepLoad<{ options: unknown[]; current: Json | null }>(props.sessionId, "client-preferences", {});
  if (load.loading && !load.data) return <p className="muted">Loading preferences…</p>;
  if (load.error || !load.data || !Array.isArray(load.data.options) || load.data.options.length === 0) {
    return <GenericStep steps={props.steps} />;
  }
  const updated = load.data.current && isRecord(load.data.current) ? load.data.current.updatedAt : null;
  return (
    <section className="adv-step-card">
      <p className="muted">
        {load.data.options.length} preference questions are configured
        {typeof updated === "string" ? ` · last saved ${dateTime(updated)}` : ""}.
      </p>
      <GenericStep steps={props.steps} />
    </section>
  );
}
